import { Validator } from 'react-admin';

import {
  type DateString,
  type DateTimeString,
  toDateString,
  toDateTimeString,
  tryToDateString,
  tryToDateTimeString,
} from '@sh4res/bo-common/utils/date';

/**
 * A fixed bound, or a function evaluated at validation time (e.g. `todayDateString`), so a form
 * left open overnight does not validate against yesterday.
 */
type Bound<T> = T | (() => T);

const resolveBound = <T extends string>(bound: Bound<T>): T =>
  typeof bound === 'function' ? bound() : bound;

const boundValidator =
  <T extends string>(
    parse: (value: unknown) => T | null,
    isOutOfBound: (value: T, bound: T) => boolean,
    argName: 'min' | 'max',
  ) =>
  (bound: Bound<T>, message: string): Validator =>
  (value: unknown) => {
    const parsed = parse(value);
    if (parsed === null) {
      return undefined;
    }
    const resolvedBound = resolveBound(bound);
    return isOutOfBound(parsed, resolvedBound)
      ? { message, args: { [argName]: resolvedBound } }
      : undefined;
  };

const minDateValidator = boundValidator(tryToDateString, (value, min) => value < min, 'min');
const maxDateValidator = boundValidator(tryToDateString, (value, max) => value > max, 'max');
const minDateTimeValidator = boundValidator(
  tryToDateTimeString,
  (value, min) => value < min,
  'min',
);
const maxDateTimeValidator = boundValidator(
  tryToDateTimeString,
  (value, max) => value > max,
  'max',
);

/**
 * Calendar day on or after `min`. Empty and unparsable values are left to other validators.
 * Accepts form values as stored by `DateInput` (`YYYY-MM-DD`) and raw API values (ISO strings).
 */
export const minDate = (min: Bound<DateString>, message = 'ra.validation.minValue') =>
  minDateValidator(min, message);

/**
 * Calendar day on or before `max`. Empty and unparsable values are left to other validators.
 */
export const maxDate = (max: Bound<DateString>, message = 'ra.validation.maxValue') =>
  maxDateValidator(max, message);

/**
 * Instant at or after `min`. Empty and unparsable values are left to other validators.
 */
export const minDateTime = (min: Bound<DateTimeString>, message = 'ra.validation.minValue') =>
  minDateTimeValidator(min, message);

/**
 * Instant at or before `max`. Empty and unparsable values are left to other validators.
 */
export const maxDateTime = (max: Bound<DateTimeString>, message = 'ra.validation.maxValue') =>
  maxDateTimeValidator(max, message);

export const MIN_SUPPORTED_DATE = toDateString('1900-01-01');
export const MAX_SUPPORTED_DATE = toDateString('9999-12-31');
const MIN_SUPPORTED_DATE_TIME = toDateTimeString('1900-01-01T00:00:00.000Z');
const MAX_SUPPORTED_DATE_TIME = toDateTimeString('9999-12-31T23:59:59.999Z');

const INVALID_DATE_MESSAGE = 'resources.bo_common.validation.invalid_date';

/**
 * Blocks days outside 1900-9999 (e.g. a mistyped year 0001), which the native input lets through.
 */
export const supportedDateRangeValidators: Validator[] = [
  minDate(MIN_SUPPORTED_DATE, INVALID_DATE_MESSAGE),
  maxDate(MAX_SUPPORTED_DATE, INVALID_DATE_MESSAGE),
];

export const supportedDateTimeRangeValidators: Validator[] = [
  minDateTime(MIN_SUPPORTED_DATE_TIME, INVALID_DATE_MESSAGE),
  maxDateTime(MAX_SUPPORTED_DATE_TIME, INVALID_DATE_MESSAGE),
];
